import { ApiError } from "@/lib/api-types";
import { env } from "@/lib/env";
import type { HttpClient } from "@/lib/http";
import { http } from "@/lib/http";

import type {
  Captcha,
  SignupConfig,
  SignupFieldError,
  SignupRequest,
  SignupResult,
} from "@/features/auth/types";

/**
 * Transport for the password-recovery + activation + signup flows. These are
 * the bare backend auth paths the legacy app used (`/forgot`, `/reset_password`,
 * `/resend_activate`, `/activate`, `/signup`, `/captcha`) — mirrored 1:1.
 * Sign-in itself goes through NextAuth (`auth-client`), not here.
 *
 * The recovery calls resolve on a 2xx and throw `ApiError` otherwise; signup
 * normalizes the backend's status-coded responses (201 ok / 400 field errors /
 * other) into a `SignupResult` the form branches on. The browser calls the
 * backend directly (ADR-0003), so there's no `x-forwarded-for` to forward.
 */
export interface AuthRepository {
  /** Request a password-reset email. Resolves even if the address is unknown
   * (the backend doesn't disclose account existence). */
  forgotPassword(email: string): Promise<void>;
  /** Set a new password using the token from the reset email link. */
  resetPassword(token: string, password: string): Promise<void>;
  /** Re-send the activation email for a pending account. */
  resendActivation(email: string): Promise<void>;
  /** Activate an account with the key from the activation email link. */
  activate(activationKey: string): Promise<void>;
  /** Read the signup site config (captcha required? activation required?). */
  getSignupConfig(): Promise<SignupConfig>;
  /** Fetch a fresh captcha image + its session token. */
  getCaptcha(): Promise<Captcha>;
  /** Create an account. Returns a normalized result, never throws on a 4xx. */
  signup(request: SignupRequest): Promise<SignupResult>;
}

interface SignupConfigDto {
  signup_captcha?: boolean;
  account_activation?: boolean;
}

function isFieldErrorArray(value: unknown): value is SignupFieldError[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "field" in item &&
        "message" in item,
    )
  );
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function createAuthRepository(client: HttpClient): AuthRepository {
  return {
    async forgotPassword(email) {
      await client.post("/forgot", { email });
    },
    async resetPassword(token, password) {
      await client.post(
        "/reset_password",
        { password },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
    async resendActivation(email) {
      await client.post("/resend_activate", { email });
    },
    async activate(activationKey) {
      await client.post("/activate", { activation_key: activationKey });
    },

    async getSignupConfig() {
      const dto = await client.get<SignupConfigDto>("/signup");
      return {
        signupCaptcha: Boolean(dto.signup_captcha),
        accountActivation: Boolean(dto.account_activation),
      };
    },

    async getCaptcha() {
      // Raw fetch (not the JSON client): the response is a binary image whose
      // session token rides in the `cache-control` header (legacy contract;
      // needs CORS `Access-Control-Expose-Headers` — see BACKEND_ASKS).
      const response = await fetch(`${env.backendUrl}/captcha`, {
        headers: { Accept: "image/*" },
      });
      const token = response.headers.get("cache-control") ?? "";
      const imageDataUrl = await blobToDataUrl(await response.blob());
      return { imageDataUrl, token };
    },

    async signup(request) {
      try {
        await client.post("/signup", request);
        return { ok: true };
      } catch (cause) {
        if (cause instanceof ApiError) {
          if (cause.status === 400 && isFieldErrorArray(cause.body)) {
            return { ok: false, fieldErrors: cause.body };
          }
          const body = cause.body;
          const formError =
            typeof body === "object" &&
            body !== null &&
            typeof (body as { error?: unknown }).error === "string"
              ? (body as { error: string }).error
              : "Something went wrong. Please try again later.";
          return { ok: false, formError };
        }
        return { ok: false, formError: "Network error. Please try again." };
      }
    },
  };
}

/** Default repository wired to the app's singleton HTTP client. */
export const authRepository = createAuthRepository(http);
